import * as React from 'react';
import { cn } from '@/lib/utils';

/** Types whose value is chosen from a browser picker rather than typed. */
const PICKER_TYPES = new Set([
  'date',
  'datetime-local',
  'month',
  'time',
  'week',
]);

/**
 * Whether the text in a number field already says `value`. An empty field
 * says 0 (what `Number('')` gives the caller) or NaN (what `parseFloat` does).
 */
function draftMatches(draft: string, value: unknown) {
  if (typeof value === 'string') return draft === value;
  if (typeof value !== 'number') return false;
  const parsed = draft.trim() === '' ? 0 : Number(draft);
  if (Number.isNaN(value)) return draft.trim() === '' || Number.isNaN(parsed);
  return parsed === value;
}

function Input({
  className,
  type,
  value,
  onClick,
  onChange,
  onBlur,
  ...props
}: React.ComponentProps<'input'>) {
  const isPicker = type !== undefined && PICKER_TYPES.has(type);

  /**
   * Callers bind number fields as `value={n}` with `Number(e.target.value)`,
   * so clearing the field hands back 0 and React writes "0" straight into it —
   * you cannot erase a value to type a new one. Keep what was typed while it
   * still means the bound value ("", "-", "1."), and show the bound value
   * again on blur.
   */
  const [draft, setDraft] = React.useState<string | null>(null);
  const tracksDraft = type === 'number' && value !== undefined;
  const shownValue =
    tracksDraft && draft !== null && draftMatches(draft, value) ? draft : value;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (tracksDraft) setDraft(event.target.value);
    onChange?.(event);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setDraft(null);
    onBlur?.(event);
  };

  /**
   * Chrome only opens the calendar when the small icon at the right edge is
   * hit — clicking the date text does nothing, which reads as a dead control.
   * `showPicker()` opens it from anywhere on the field.
   */
  const handleClick = (event: React.MouseEvent<HTMLInputElement>) => {
    onClick?.(event);
    if (!isPicker || event.defaultPrevented) return;
    try {
      event.currentTarget.showPicker();
    } catch {
      // Not supported, or the browser refused (no user activation). The
      // native icon still works, so there is nothing to recover from.
    }
  };

  return (
    <input
      type={type}
      data-slot="input"
      value={shownValue}
      onClick={handleClick}
      onChange={handleChange}
      onBlur={handleBlur}
      className={cn(
        'flex h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none md:text-sm',
        'file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium',
        'placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground',
        'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
        // The whole field is the target, and the native icon stretches to fill
        // it so a click anywhere lands on something that opens the picker.
        isPicker && 'cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-60',
        className
      )}
      {...props}
    />
  );
}

export { Input };
